// @file Analytics helper for the landside marketing pages (the activation → sign-up funnel).
//
// Wraps `trackEvent` (bits/analytics) so every landside event carries a consistent
// `category` and a `page` property, and dual-emits each event to the main Padlet PostHog
// project (see `captureLandsidePostHogEvent`) so funnels can be built there without waiting
// on the warehouse. Two problems the wrapper solves:
//   1. Today the real homepage, all five landing pages, and most of their CTAs collide
//      under the single category `'Homepage'`, so per-page funnels are impossible.
//   2. Event names drift per page ('Sandbox Landside', 'Landside template gallery', …),
//      so cross-page funnels can't join.
// The wrapper produces the category in exactly one place and always injects `page`, so
// callers cannot reintroduce that drift.
//
// Two known funnel gaps are tracked elsewhere — do not re-scope them here:
//   - Anonymous→identified stitching (client `identify()`/`.alias()` + a 'Signed up'
//     event) is MAR-582; without it the funnel ends at "signup step submitted".
//   - Delivery reliability: `trackEvent` no-ops before `window.load` and click-then-navigate
//     can drop the beacon, so CTA clicks undercount relative to impressions. The fix lives
//     in the shared bootstrap (_pepin_analytics.html.erb) and pulls against ENG-40868
//     (defer analytics off the homepage boot path) — coordinate with that ticket's owner.
import { trackEvent } from '@@/bits/analytics'
import { capturePostHogEvent, warmPostHog } from '@@/bits/posthog_analytics'
import type { LandsidePage } from '@@/pinia/landside_navigation'

// Only the five funnel landing pages get a dedicated `Landside <Page>` category. Every
// other landside route (home, product, pricing, dynamic landing pages, arcade) keeps the
// historical `'Homepage'` category so we don't silently recategorize non-funnel traffic —
// the precise page is always available in the `page` property regardless.
const LANDSIDE_EVENT_CATEGORY_BY_PAGE: Partial<Record<LandsidePage, string>> = {
  board: 'Landside Board',
  sandbox: 'Landside Sandbox',
  subscriptions: 'Landside Subscriptions',
  templates: 'Landside Templates',
  education: 'Landside Education',
}
const DEFAULT_LANDSIDE_CATEGORY = 'Homepage'

// Where on the page an event originated. Bounded set → low cardinality, easy to group.
enum LandsideSection {
  Header = 'header',
  Hero = 'hero',
  Pricing = 'pricing',
  Faq = 'faq',
  Gallery = 'gallery',
  Examples = 'examples',
  Testimonials = 'testimonials',
  // The closing "Get started" CTA band some landing pages end on — above the footer proper.
  GetStarted = 'get_started',
  Footer = 'footer',
  // The nav side panel only renders on mobile-phone-portrait viewports (the hamburger menu),
  // so the slug says so — a bare 'nav' reads as if it could be the desktop tab bar.
  MobilePhoneNav = 'mobile_phone_nav',
}

// Which call-to-action fired. New conversion clicks all use the action `'Clicked CTA'` and
// are disambiguated by this slug, so the funnel key is `cta` (not a per-button action string).
enum LandsideCta {
  MakeBoard = 'make_board',
  MakeSandbox = 'make_sandbox',
  SignUp = 'sign_up',
  LogIn = 'log_in',
  GetQuote = 'get_quote',
  SelectPlan = 'select_plan',
  ComparePlans = 'compare_plans',
  ViewPlans = 'view_plans',
  ViewExample = 'view_example',
  UseTemplate = 'use_template',
}

// The generic action every new landside conversion click uses (disambiguated by `cta`).
const LANDSIDE_CLICK_CTA_ACTION = 'Clicked CTA'
// The action every landside section-impression uses (disambiguated by `section`).
const LANDSIDE_VIEW_SECTION_ACTION = 'Viewed section'

interface LandsideEventProps {
  section?: LandsideSection
  cta?: LandsideCta
  destination?: string
  loggedIn?: boolean
  plan?: string
  [key: string]: unknown
}

// Landside-specific wrappers over the shared PostHog helpers (bits/posthog_analytics), so
// every landside capture is attributed to this module and the transport behavior stays
// identical to every other surface capturing into the main Padlet PostHog project. Landside
// pages warm on mount because CTA clicks navigate immediately — see warmPostHog.
function warmLandsidePostHog(): void {
  warmPostHog('landside_analytics')
}

// The PostHog event name is the Rudder `action` verbatim and the Rudder `category` rides
// along as a property, so the two destinations stay joinable on identical
// `page`/`section`/`cta` values. Not exported: every landside PostHog event goes out through
// one of the two trackers below, and `$pageview` is captured by the PostHog client itself.
function captureLandsidePostHogEvent(event: string, properties: Record<string, unknown>): Promise<void> {
  return capturePostHogEvent(event, properties, 'landside_analytics')
}

/**
 * The analytics category for a landside page. Shared by CTA/section events
 * (trackLandsideEvent) and the SPA pageviews in the landside navigation store, so pageviews
 * and clicks for the same page always join on category — and so no other caller can
 * accidentally recategorize non-funnel pages away from 'Homepage'.
 */
function landsideEventCategoryForPage(page: LandsidePage): string {
  return LANDSIDE_EVENT_CATEGORY_BY_PAGE[page] ?? DEFAULT_LANDSIDE_CATEGORY
}

/**
 * The one dual-emit body both trackers share, so the RudderStack/PostHog mirror shape can
 * never drift between them. Normalizes `destination` (PII safety is enforced here, not
 * opt-in per caller) and sends the same props to both pipelines. `label`/`value` are
 * Rudder-only: the strings legacy events carry there (full example URLs, template titles)
 * are high-cardinality and sometimes href-shaped, so the PostHog side never receives them —
 * PostHog properties stay the low-cardinality funnel join keys. `category` is spread last
 * so a stray `category` in props can never override the caller-derived value.
 */
function emitLandsideDualEvent(
  category: string,
  action: string,
  props: LandsideEventProps & { page: LandsidePage },
  label: string | null,
  value: number | null,
): void {
  const safeProps =
    props.destination != null ? { ...props, destination: normalizeDestination(props.destination) } : props
  trackEvent(category, action, label, value, safeProps)
  captureLandsidePostHogEvent(action, { ...safeProps, category })
}

/**
 * Track a landside event. Injects a stable `page` property and derives the category from
 * the page, so cross-page funnels join on `page`/`section`/`cta` instead of brittle
 * category strings. `label`/`value` are reserved for the few existing high-cardinality
 * human strings (template title, search query) — never emails, user ids, or full hrefs —
 * and reach RudderStack only (see emitLandsideDualEvent).
 */
function trackLandsideEvent(
  page: LandsidePage,
  action: string,
  props: LandsideEventProps = {},
  label: string | null = null,
  value: number | null = null,
): void {
  const category = landsideEventCategoryForPage(page)
  // `page` is spread last so a stray `page` in props can never override the centrally
  // derived value — that would silently reintroduce the drift this module exists to prevent.
  emitLandsideDualEvent(category, action, { ...props, page }, label, value)
}

/**
 * Dual-emit for pre-existing landside events that must keep their historical Rudder
 * category and action ('Quotation form' / 'Opened form', 'Sandbox Landside' / 'Clicked
 * signup', …) because existing dashboards group by them. RudderStack receives the event
 * unchanged (plus the backfilled funnel props); PostHog receives the same mirror shape as
 * `trackLandsideEvent` (event name = action, category as a property, no label/value), so
 * backfilled legacy events join the same PostHog funnels as the new taxonomy on
 * `page`/`section`/`cta`.
 */
function trackLandsideLegacyEvent(
  category: string,
  action: string,
  props: LandsideEventProps & { page: LandsidePage },
  label: string | null = null,
  value: number | null = null,
): void {
  emitLandsideDualEvent(category, action, props, label, value)
}

// Path segments that are known static route words on landside CTA destinations. Anything
// NOT in this vocabulary is treated as a dynamic — and therefore potentially identifying —
// segment and collapsed to `:id`. The allowlist fails closed: a username, wall slug, board
// id, or email in a path can never reach analytics just because it doesn't look "id-like".
// Extend the set when a CTA legitimately targets a new static route.
const STATIC_DESTINATION_SEGMENTS = new Set([
  'auth',
  'signup',
  'login',
  'quick-create',
  'site',
  'board',
  'sandbox',
  'subscriptions',
  'templates',
  'education',
  'product',
  'pricing',
  'features',
  'account',
  'create',
  'classroom',
  'premium',
  'upgrade',
  'dashboard',
  'gallery',
  'whatsnew',
  'help',
  'careers',
  'referrals',
])

// Sentinel for destinations that carry no safe path: mailto:/tel:/other schemes and
// protocol-relative URLs (whose "path" starts with a foreign host).
const EXTERNAL_DESTINATION = ':external'

/**
 * Reduce a CTA href to a low-cardinality, PII-free `destination`. Paths keep only
 * allowlisted static route segments — query and hash are dropped, the origin of absolute
 * http(s) URLs is dropped, and every non-allowlisted segment collapses to `:id` (e.g.
 * `/premium/upgrade/gold-1yr` → `/premium/upgrade/:id`). Scheme'd non-http destinations
 * (mailto:, tel:, …) and protocol-relative URLs reduce to the `:external` sentinel, so
 * emails, phone numbers, and hosts never reach analytics.
 */
function normalizeDestination(href: string): string {
  if (!href) return ''
  let path = href
  if (/^https?:\/\//i.test(path)) {
    // Absolute URL: drop the origin (host included), keep the path for the allowlist below.
    path = path.replace(/^https?:\/\/[^/]*/i, '')
  } else if (path.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(path)) {
    return EXTERNAL_DESTINATION
  }
  const normalized = path
    .split(/[?#]/)[0]
    .split('/')
    .map((segment) => {
      if (segment === '') return segment
      const lowered = segment.toLowerCase()
      return STATIC_DESTINATION_SEGMENTS.has(lowered) ? lowered : ':id'
    })
    .join('/')
  return normalized || '/'
}

export {
  LandsideSection,
  LandsideCta,
  LANDSIDE_CLICK_CTA_ACTION,
  LANDSIDE_VIEW_SECTION_ACTION,
  LandsideEventProps,
  warmLandsidePostHog,
  landsideEventCategoryForPage,
  trackLandsideEvent,
  trackLandsideLegacyEvent,
  normalizeDestination,
}
